"use client";

import { useSyncExternalStore } from "react";

const KEY = "ks_booking_selection";

type Listener = () => void;
const listeners = new Set<Listener>();

// Cache the parsed snapshot so useSyncExternalStore's getSnapshot returns a
// stable reference when the underlying value hasn't changed.
let cachedRaw: string | null | undefined = undefined;
let cachedValue: BookingSelection | null = null;

function readFromStorage(): BookingSelection | null {
  if (typeof window === "undefined") return null;
  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(KEY);
  } catch {
    raw = null;
  }
  if (raw === cachedRaw) return cachedValue;
  cachedRaw = raw;
  try {
    const parsed = raw ? (JSON.parse(raw) as BookingSelection) : null;
    cachedValue =
      parsed && (parsed.branchId || parsed.studioId) ? parsed : null;
  } catch {
    cachedValue = null;
  }
  return cachedValue;
}

function emit() {
  listeners.forEach((l) => l());
}

export interface BookingSelection {
  branchId?: string;
  studioId?: string;
}

export function getBookingSelection(): BookingSelection | null {
  return readFromStorage();
}

export function setBookingSelection(sel: BookingSelection): void {
  if (typeof window === "undefined") return;
  try {
    const raw = JSON.stringify(sel);
    window.sessionStorage.setItem(KEY, raw);
    cachedRaw = raw;
    cachedValue = sel && (sel.branchId || sel.studioId) ? sel : null;
    emit();
  } catch {
    /* ignore */
  }
}

export function clearBookingSelection(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(KEY);
    cachedRaw = null;
    cachedValue = null;
    emit();
  } catch {
    /* ignore */
  }
}

function subscribe(cb: Listener): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Live, hydration-safe view of the persisted branch/studio selection. */
export function useBookingSelection(): BookingSelection | null {
  return useSyncExternalStore(
    subscribe,
    getBookingSelection,
    () => null,
  );
}
